import type { Order } from '@modules/commercetools'
import axios from 'axios'
import { v7 } from 'uuid'
import { CartFragment, Maybe } from '../types/commercetools-schema'
import { GaCartFragment } from '../types/gaTracking'
import { getCartDiscounts, getCartTotals } from '../utils'
import { Discount } from '../utils/checkout/helpers/types'
import {
  concatDiscountCodesWithSpace,
  formatPrice,
  gaItemFields,
  getItemsWithValues,
} from './helpers'

// First-party analytics events sent to `/api/events`

export const EVENTS_ENDPOINT = '/api/events'

export type TrackEventName =
  | 'Cart Viewed'
  | 'Product Removed'
  | 'Cart Updated'
  | 'Checkout Started'
  | 'Shipping Info Added'
  | 'Payment Info Added'
  | 'Order Placed'

export type EventIdentity = {
  customerId?: Maybe<string>
  anonymousId?: Maybe<string>
}

export type CartEventProperties = {
  cart_id: string
  value: number
  currency: string
  coupon: string
  discount: number
  items: gaItemFields[]
}

type TrackableCart = GaCartFragment & EventIdentity

const getLineItemDiscounts = (cart: TrackableCart): Discount[] => {
  try {
    return getCartDiscounts(cart as CartFragment).lineItemDiscounts
  } catch {
    return []
  }
}

export const toCartEventProperties = (
  cart: TrackableCart,
  discounts: Discount[] = getLineItemDiscounts(cart)
): CartEventProperties => {
  const { total, shipping, discount, discountedShipping } = getCartTotals(
    cart as CartFragment
  )

  return {
    cart_id: cart.id ?? '',
    value: formatPrice(total - (discountedShipping ?? shipping)),
    currency: cart.totalPrice?.currencyCode ?? '',
    coupon: concatDiscountCodesWithSpace(discounts),
    discount: formatPrice(discount),
    items: getItemsWithValues(cart.lineItems),
  }
}

// Sends a track event. Never throws any errors
export const sendTrackEvent = async (
  event: TrackEventName,
  properties: Record<string, unknown>,
  identity: EventIdentity = {}
): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  const now = new Date().toISOString()

  try {
    await axios.post(
      EVENTS_ENDPOINT,
      {
        type: 'track',
        event,
        messageId: v7(),
        originalTimestamp: now,
        sentAt: now,
        context: {
          channel: 'web',
          userId: identity.customerId ?? undefined,
          anonymousId: identity.anonymousId ?? undefined,
        },
        properties,
      },
      { timeout: 5000 }
    )
  } catch (e) {
    console.log(e)
  }
}

export const trackCartViewed = (params: {
  cart?: TrackableCart
  discounts?: Discount[]
}) => {
  const { cart, discounts } = params
  if (!cart?.id) {
    return Promise.resolve()
  }

  return sendTrackEvent(
    'Cart Viewed',
    toCartEventProperties(cart, discounts),
    cart
  )
}

// Fires "Product Removed" with the removed line items
export const trackProductsRemoved = (params: {
  prevCart?: Maybe<TrackableCart>
  lineItemIds: string[]
  quantity?: number
}) => {
  const { prevCart, lineItemIds, quantity } = params
  if (!prevCart?.id || !lineItemIds.length) {
    return Promise.resolve()
  }

  const lineItemIdSet = new Set(lineItemIds)
  const removedLineItems =
    prevCart.lineItems?.filter((lineItem) => lineItemIdSet.has(lineItem.id)) ??
    []

  const items = getItemsWithValues(removedLineItems)
    .map((item) =>
      typeof quantity === 'number' ? { ...item, quantity } : item
    )
    .filter((item) => (item.quantity ?? 0) > 0)
  if (!items.length) {
    return Promise.resolve()
  }

  const value = items.reduce(
    (total, item) => total + (item.price ?? 0) * (item.quantity ?? 0),
    0
  )

  return sendTrackEvent(
    'Product Removed',
    {
      cart_id: prevCart.id,
      currency: items[0]?.currency || prevCart.totalPrice?.currencyCode || '',
      value,
      items,
    },
    prevCart
  )
}

// Fires "Product Removed" when a line item's quantity is lowered
export const trackCartItemQuantityChanged = (params: {
  prevCart?: Maybe<TrackableCart>
  lineItemId: string
  quantity: number
}) => {
  const { prevCart, lineItemId, quantity } = params
  const prevQuantity =
    prevCart?.lineItems?.find((lineItem) => lineItem.id === lineItemId)
      ?.quantity ?? 0

  if (quantity >= prevQuantity) {
    return Promise.resolve()
  }

  return trackProductsRemoved({
    prevCart,
    lineItemIds: [lineItemId],
    quantity: prevQuantity - quantity,
  })
}

// Fires "Cart Updated" with a snapshot of the cart after its items changed.
export const trackCartUpdated = (params: { cart?: Maybe<TrackableCart> }) => {
  const { cart } = params
  if (!cart?.id) {
    return Promise.resolve()
  }

  return sendTrackEvent('Cart Updated', toCartEventProperties(cart), cart)
}

export const trackCheckoutStarted = (params: {
  cart?: TrackableCart
  discounts?: Discount[]
  checkoutType: string
}) => {
  const { cart, discounts, checkoutType } = params
  if (!cart?.id) {
    return Promise.resolve()
  }

  return sendTrackEvent(
    'Checkout Started',
    { ...toCartEventProperties(cart, discounts), checkout_type: checkoutType },
    cart
  )
}

// Slim event: intentionally excludes any address information (PII).
export const trackShippingInfoAdded = (params: {
  cart?: Maybe<TrackableCart>
  checkoutType: string
}) => {
  const { cart, checkoutType } = params
  if (!cart?.id) {
    return Promise.resolve()
  }

  return sendTrackEvent(
    'Shipping Info Added',
    { cart_id: cart.id, checkout_type: checkoutType },
    cart
  )
}

// Slim event: only the payment type, no payment details (PII).
export const trackPaymentInfoAdded = (params: {
  cart?: Maybe<TrackableCart>
  paymentType?: string
  checkoutType: string
}) => {
  const { cart, paymentType, checkoutType } = params
  if (!cart?.id) {
    return Promise.resolve()
  }

  return sendTrackEvent(
    'Payment Info Added',
    {
      cart_id: cart.id,
      payment_type: paymentType ?? '',
      checkout_type: checkoutType,
    },
    cart
  )
}

// Order details added to "Order Placed" on top of `orderNumber`. Returns `{}`
export const buildOrderPlacedProperties = (params: {
  order?: Order
  discounts?: Discount[]
  paymentType?: string
  checkoutType: string
}) => {
  const { order, discounts, paymentType, checkoutType } = params
  if (!order) {
    return {}
  }

  try {
    return getOrderPlacedProperties(order, discounts, paymentType, checkoutType)
  } catch (e) {
    console.log(e)
    return {}
  }
}

const getOrderPlacedProperties = (
  order: Order,
  discounts: Discount[] | undefined,
  paymentType: string | undefined,
  checkoutType: string
) => {
  const { total, discount, tax, shipping, discountedShipping } = getCartTotals({
    shippingInfo: order.shippingInfo,
    taxedPrice: order.taxedPrice,
    totalPrice: order.totalPrice,
    custom: order.cart?.custom,
    lineItems: order.lineItems as Partial<Order['lineItems']> as Partial<
      CartFragment['lineItems']
    >,
  } as CartFragment)

  return {
    order_id: order.id,
    cart_id: order.cart?.id ?? undefined,
    value: formatPrice(total),
    shipping: formatPrice(discountedShipping ?? shipping),
    shipping_tier: order.shippingInfo?.shippingMethodName ?? '',
    tax: formatPrice(tax),
    discount: formatPrice(discount),
    coupon: concatDiscountCodesWithSpace(discounts ?? []),
    currency: order.totalPrice?.currencyCode ?? '',
    payment_type: paymentType ?? '',
    checkout_type: checkoutType,
    items: getItemsWithValues(order.lineItems),
  }
}

export const trackOrderPlaced = (params: {
  order?: Order
  orderNumber?: Maybe<string>
  discounts?: Discount[]
  paymentType?: string
  checkoutType: string
}) => {
  const { order } = params
  const orderNumber = params.orderNumber ?? order?.orderNumber
  if (!order || !orderNumber) {
    return Promise.resolve()
  }

  return sendTrackEvent(
    'Order Placed',
    { orderNumber, ...buildOrderPlacedProperties(params) },
    order
  )
}
